import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { PurchaseOrder, PurchaseOrderItem } from '../../../../models/PurchaseOrder';
import {
  PurchaseOrderQuotation,
  PurchaseOrderQuotationHeader,
  PurchaseOrderQuotationLine,
  CreateQuotationDto,
  CreateQuotationHeaderDto,
  QuotationDocument,
  SelectWinnerDto
} from '../../../../models/PurchaseOrderQuotation';
import { Supplier } from '../../../../models/Supplier';
import { PurchaseOrderQuotationsService } from '../../../../services/purchase-order-quotations.service';
import { PurchaseOrderService } from '../../../../services/purchase-order.service';
import { SuppliersService } from '../../../../services/suppliers.service';
import { AuthService } from '../../../../services/auth.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { CURRENCY_OPTIONS, CurrencyCode } from '../../../../shared/constants/currency';
import { translateShoppingStatus } from '../../../../shared/constants/shopping-status';
import {
  QUOTATION_DOCUMENT_CATEGORY_OPTIONS,
  QuotationDocumentCategory
} from '../../../../shared/constants/quotation-document-categories';

const MAX_PROPOSALS = 3;

/** Linha no modal «vencedor»: preço vem só do orçamento da proposta vencedora (somente leitura na UI). */
interface WinnerLineFormRow {
  purchaseOrderItemId: number;
  label: string;
  unit: string;
  quantityRequested: number;
  actualUnitPrice: number | null;
  quantityApproved: number | null;
}

/** Formulário local do upload de anexo da proposta. */
interface QuotationDocumentFormModel {
  file: File | null;
  category: QuotationDocumentCategory;
  description: string;
}

/** Formulário local da proposta (valores numéricos estáveis para inputs). */
interface ProposalFormModel {
  id?: number;
  supplierId: number | null;
  quotationNumber: string;
  quotationDate: string;
  validUntil: string;
  currencyCode: string;
  itemsSubtotal: number | null;
  discountAmount: number | null;
  freightAmount: number | null;
  otherCostsAmount: number | null;
  taxAmount: number | null;
  totalAmount: number | null;
  paymentTermDays: number | null;
  deliveryLeadTimeDays: number | null;
  status: string;
  notes: string;
}

@Component({
  selector: 'app-purchase-order-quotations',
  templateUrl: './purchase-order-quotations.component.html',
  styleUrls: ['./purchase-order-quotations.component.css']
})
export class PurchaseOrderQuotationsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  readonly maxProposals = MAX_PROPOSALS;
  readonly currencyOptions = CURRENCY_OPTIONS;
  readonly documentCategoryOptions = QUOTATION_DOCUMENT_CATEGORY_OPTIONS;
  /** Tamanho máximo de cada anexo (15 MB). Backend valida em definitivo. */
  readonly maxDocumentSizeBytes = 15 * 1024 * 1024;
  /** Exposto para uso no template (`{{ translateStatus(q.status) }}`). */
  readonly translateStatus = translateShoppingStatus;

  purchaseOrderId: number | null = null;
  purchaseOrder: PurchaseOrder | null = null;
  header: PurchaseOrderQuotationHeader | null = null;
  proposals: PurchaseOrderQuotation[] = [];
  suppliers: Supplier[] = [];

  isLoading = false;
  isSavingHeader = false;
  isSavingProposal = false;
  isSelectingWinner = false;
  staffId: number | null = null;

  showProposalModal = false;
  proposalModalMode: 'create' | 'edit' = 'create';
  proposalForm: ProposalFormModel = this.emptyProposalForm();

  showWinnerModal = false;
  winnerReason = '';
  winnerQuotationId: number | null = null;
  winnerLines: WinnerLineFormRow[] = [];
  /** Carrega GET da proposta para obter valores por linha do orçamento. */
  isLoadingWinnerLines = false;
  private winnerModalLoadGeneration = 0;

  showCancelHeaderModal = false;
  cancelHeaderReason = '';

  showCancelProposalModal = false;
  cancelProposalReason = '';
  cancelProposalId: number | null = null;

  // ===== PORTAL DE ANEXOS DA PROPOSTA =====
  showDocumentsModal = false;
  documentsModalQuotation: PurchaseOrderQuotation | null = null;
  documents: QuotationDocument[] = [];
  isLoadingDocuments = false;
  isUploadingDocument = false;
  deletingDocumentId: number | null = null;
  documentForm: QuotationDocumentFormModel = this.emptyDocumentForm();
  /** Map quotationId → quantidade de anexos (badge na linha da proposta). */
  documentsCountByQuotation: Record<number, number> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private purchaseOrderService: PurchaseOrderService,
    private quotationsService: PurchaseOrderQuotationsService,
    private suppliersService: SuppliersService,
    private authService: AuthService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.authService.getStaffId().pipe(takeUntil(this.destroy$)).subscribe((id) => {
      this.staffId = id;
    });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(['/purchase-orders']);
        return;
      }
      this.purchaseOrderId = Number(id);
      if (!Number.isFinite(this.purchaseOrderId) || this.purchaseOrderId <= 0) {
        this.router.navigate(['/purchase-orders']);
        return;
      }
      this.reloadAll();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadAll(): void {
    if (!this.purchaseOrderId) return;
    this.isLoading = true;

    forkJoin({
      order: this.purchaseOrderService.getPurchaseOrderComplete(this.purchaseOrderId).pipe(
        catchError(() => of(null))
      ),
      suppliers: this.suppliersService.getAllPaged(1, 500).pipe(
        catchError(() => of({ items: [] as Supplier[], page: 1, pageSize: 500, totalItems: 0, totalPages: 0 }))
      )
    })
      .pipe(
        switchMap(({ order, suppliers }) => {
          this.purchaseOrder = order;
          this.suppliers = suppliers.items || [];
          return this.quotationsService.listHeaders(this.purchaseOrderId!, 1, 50);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (page) => {
          const candidates = (page.items || []).filter((h) => !this.isHeaderCancelled(h));
          const headerPick =
            candidates.find((h) => this.isHeaderOpen(h)) ??
            candidates.sort((a, b) => b.id - a.id)[0] ??
            null;

          if (headerPick) {
            this.loadHeaderDetail(headerPick.id);
          } else {
            this.header = null;
            this.proposals = [];
            this.isLoading = false;
          }
        },
        error: () => {
          this.errorService.showErrorMessage('Não foi possível carregar as cotações.');
          this.isLoading = false;
        }
      });
  }

  private loadHeaderDetail(headerId: number): void {
    this.quotationsService
      .getHeaderById(headerId, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (h) => {
          this.header = h;
          const embedded = h.proposals || [];
          if (embedded.length > 0) {
            this.proposals = embedded.slice().sort((a, b) => a.id - b.id);
            this.isLoading = false;
          } else {
            this.refreshProposalsList(headerId);
          }
        },
        error: () => {
          this.errorService.showErrorMessage('Não foi possível carregar o processo de cotação.');
          this.isLoading = false;
        }
      });
  }

  private refreshProposalsList(headerId: number): void {
    this.quotationsService
      .listQuotationsForHeader(headerId, 1, 50)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (p) => {
          this.proposals = (p.items || []).slice().sort((a, b) => a.id - b.id);
          this.isLoading = false;
        },
        error: () => {
          this.errorService.showErrorMessage('Não foi possível carregar as propostas.');
          this.isLoading = false;
        }
      });
  }

  goBack(): void {
    if (this.purchaseOrderId) {
      this.router.navigate(['/purchase-orders', this.purchaseOrderId]);
    } else {
      this.router.navigate(['/purchase-orders']);
    }
  }

  startQuotationProcess(): void {
    if (!this.purchaseOrderId) {
      this.errorService.showErrorMessage(
        'Pedido inválido.'
      );
      return;
    }
    const createdByStaffId = this.purchaseOrder?.requestedByStaffId ?? this.staffId;
    if (!createdByStaffId) {
      this.errorService.showErrorMessage('Não foi possível identificar o colaborador responsável pela cotação.');
      return;
    }
    const now = new Date();
    const valid = new Date(now);
    valid.setMonth(valid.getMonth() + 2);

    const dto: CreateQuotationHeaderDto = {
      purchaseOrderId: this.purchaseOrderId,
      headerNumber: this.buildHeaderNumber(),
      headerDate: now.toISOString(),
      validUntil: valid.toISOString(),
      createdByStaffId,
      status: 'Aberto'
    };

    this.isSavingHeader = true;
    this.quotationsService
      .createHeader(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (h) => {
          this.isSavingHeader = false;
          this.errorService.showSuccessMessage('Processo de cotação iniciado.');
          this.header = h;
          this.proposals = [];
          this.loadHeaderDetail(h.id);
        },
        error: () => {
          this.isSavingHeader = false;
          this.errorService.showErrorMessage('Não foi possível iniciar o processo de cotação.');
        }
      });
  }

  private buildHeaderNumber(): string {
    const y = new Date().getFullYear();
    const n = this.purchaseOrderId ?? 0;
    return `COT-${y}-${String(n).padStart(4, '0')}`;
  }

  openCreateProposal(): void {
    if (!this.header || this.proposals.length >= MAX_PROPOSALS || !this.canMutateProposals()) {
      return;
    }
    this.router.navigate(['/purchase-orders', this.purchaseOrderId, 'quotations', 'new-proposal']);
  }

  openEditProposal(q: PurchaseOrderQuotation): void {
    if (!this.canMutateProposals()) return;
    this.proposalModalMode = 'edit';
    this.proposalForm = {
      id: q.id,
      supplierId: q.supplierId,
      quotationNumber: q.quotationNumber || '',
      quotationDate: this.toInputIso(q.quotationDate),
      validUntil: this.toInputIso(q.validUntil),
      currencyCode: q.currencyCode || CurrencyCode.BRL,
      itemsSubtotal: q.itemsSubtotal ?? null,
      discountAmount: q.discountAmount ?? null,
      freightAmount: q.freightAmount ?? null,
      otherCostsAmount: q.otherCostsAmount ?? null,
      taxAmount: q.taxAmount ?? null,
      totalAmount: q.totalAmount ?? null,
      paymentTermDays: q.paymentTermDays ?? 30,
      deliveryLeadTimeDays: q.deliveryLeadTimeDays ?? 7,
      status: q.status || 'Received',
      notes: q.notes || ''
    };
    this.showProposalModal = true;
  }

  closeProposalModal(): void {
    if (this.isSavingProposal) return;
    this.showProposalModal = false;
  }

  submitProposal(): void {
    if (!this.header || !this.staffId) {
      this.errorService.showErrorMessage('Dados incompletos para salvar a proposta.');
      return;
    }
    const f = this.proposalForm;
    const supplierId = Number(f.supplierId || 0);
    let quotationNumber: string;
    if (this.proposalModalMode === 'edit') {
      quotationNumber = (f.quotationNumber || '').trim();
      if (!quotationNumber) {
        this.errorService.showErrorMessage('Número da proposta ausente. Reabra a proposta para editar.');
        return;
      }
    } else {
      quotationNumber = this.generateQuotationNumber();
    }
    if (!supplierId) {
      this.errorService.showErrorMessage('Selecione o fornecedor.');
      return;
    }

    const dtoBase = {
      purchaseOrderQuotationHeaderId: this.header.id,
      supplierId,
      quotationNumber,
      quotationDate: this.fromInputIso(f.quotationDate),
      validUntil: this.fromInputIso(f.validUntil),
      currencyCode: (f.currencyCode || CurrencyCode.BRL).trim() || CurrencyCode.BRL,
      itemsSubtotal: Number(f.itemsSubtotal ?? 0),
      discountAmount: Number(f.discountAmount ?? 0),
      freightAmount: Number(f.freightAmount ?? 0),
      otherCostsAmount: Number(f.otherCostsAmount ?? 0),
      taxAmount: Number(f.taxAmount ?? 0),
      totalAmount: Number(f.totalAmount ?? this.computeTotal(f)),
      paymentTermDays: Number(f.paymentTermDays ?? 0),
      deliveryLeadTimeDays: Number(f.deliveryLeadTimeDays ?? 0),
      status: (f.status || 'Received').trim() || 'Received',
      isWinner: false,
      notes: f.notes?.trim() || undefined
    };

    this.isSavingProposal = true;
    const prev = this.proposals.find((p) => p.id === f.id);
    const req$ =
      this.proposalModalMode === 'create'
        ? this.quotationsService.createQuotation({ ...(dtoBase as CreateQuotationDto), isWinner: false })
        : this.quotationsService.updateQuotation(f.id!, {
            ...(dtoBase as CreateQuotationDto),
            isWinner: prev?.isWinner ?? false
          });

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSavingProposal = false;
        this.showProposalModal = false;
        this.errorService.showSuccessMessage(
          this.proposalModalMode === 'create' ? 'Proposta registrada.' : 'Proposta atualizada.'
        );
        if (this.header) this.refreshProposalsList(this.header.id);
      },
      error: () => {
        this.isSavingProposal = false;
        this.errorService.showErrorMessage('Não foi possível salvar a proposta.');
      }
    });
  }

  recalculateProposalTotal(): void {
    this.proposalForm.totalAmount = this.computeTotal(this.proposalForm);
  }

  private computeTotal(f: ProposalFormModel): number {
    const sub = Number(f.itemsSubtotal ?? 0);
    const disc = Number(f.discountAmount ?? 0);
    const freight = Number(f.freightAmount ?? 0);
    const other = Number(f.otherCostsAmount ?? 0);
    const tax = Number(f.taxAmount ?? 0);
    return Math.round((sub - disc + freight + other + tax + Number.EPSILON) * 100) / 100;
  }

  openWinnerModal(q: PurchaseOrderQuotation): void {
    if (!this.canSelectWinner()) return;
    const items = this.purchaseOrder?.items || [];
    if (!items.length) {
      this.errorService.showErrorMessage(
        'Este pedido não possui itens. Não é possível enviar a escolha do vencedor sem linhas no pedido.'
      );
      return;
    }
    const loadGen = ++this.winnerModalLoadGeneration;
    this.winnerQuotationId = q.id;
    this.winnerReason = '';
    this.winnerLines = [];
    this.isLoadingWinnerLines = true;
    this.showWinnerModal = true;

    this.quotationsService
      .getQuotationById(q.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          if (loadGen !== this.winnerModalLoadGeneration || !this.showWinnerModal) {
            return;
          }
          this.isLoadingWinnerLines = false;
          const merged: PurchaseOrderQuotation = {
            ...q,
            ...detail,
            itemsSubtotal: detail.itemsSubtotal ?? q.itemsSubtotal,
            totalAmount: detail.totalAmount ?? q.totalAmount,
            lines: detail.lines?.length ? detail.lines : q.lines
          };
          this.winnerLines = this.buildWinnerLinesFromQuotation(merged, items);
          if (!this.winnerLinesHaveValidPrices()) {
            this.errorService.showErrorMessage(
              'Não foi possível obter o preço unitário do orçamento vencedor para todos os itens. Confira se a proposta tem linhas com valores ou subtotal de itens cadastrado.'
            );
          }
        },
        error: () => {
          if (loadGen !== this.winnerModalLoadGeneration) {
            return;
          }
          this.isLoadingWinnerLines = false;
          this.errorService.showErrorMessage('Não foi possível carregar os valores da proposta selecionada.');
          this.closeWinnerModal();
        }
      });
  }

  closeWinnerModal(): void {
    if (this.isSelectingWinner) return;
    this.winnerModalLoadGeneration++;
    this.showWinnerModal = false;
    this.winnerQuotationId = null;
    this.winnerLines = [];
    this.isLoadingWinnerLines = false;
  }

  winnerLinesHaveValidPrices(): boolean {
    return (
      this.winnerLines.length > 0 &&
      this.winnerLines.every((r) => {
        const p = Number(r.actualUnitPrice);
        const q = Number(r.quantityApproved);
        return Number.isFinite(p) && p >= 0 && Number.isFinite(q) && q > 0;
      })
    );
  }

  /** Total da linha do vencedor: `actualUnitPrice * quantityApproved`. Null quando algum campo é inválido. */
  winnerLineTotal(row: WinnerLineFormRow): number | null {
    const price = Number(row?.actualUnitPrice);
    const qty = Number(row?.quantityApproved);
    if (!Number.isFinite(price) || !Number.isFinite(qty)) return null;
    return Math.round((price * qty + Number.EPSILON) * 100) / 100;
  }

  /** Soma dos totais por item — preço total da compra exibida no rodapé do modal. */
  get winnerOverallTotal(): number {
    return this.winnerLines.reduce((acc, row) => acc + (this.winnerLineTotal(row) ?? 0), 0);
  }

  private buildWinnerLinesFromQuotation(
    quotation: PurchaseOrderQuotation,
    poItems: PurchaseOrderItem[]
  ): WinnerLineFormRow[] {
    const lineByPoItemId = new Map<number, PurchaseOrderQuotationLine>();
    for (const ln of quotation.lines || []) {
      lineByPoItemId.set(ln.purchaseOrderItemId, ln);
    }

    return poItems.map((it) => {
      const ln = lineByPoItemId.get(it.id);
      let unitFromQuote =
        ln != null ? this.unitPriceFromQuotationLine(ln) : null;
      if (unitFromQuote == null) {
        unitFromQuote = this.inferredUnitPriceFromItemsSubtotal(quotation, it, poItems);
      }
      const rounded =
        unitFromQuote != null && Number.isFinite(unitFromQuote)
          ? Math.round((unitFromQuote + Number.EPSILON) * 100) / 100
          : null;
      const qtyFromItem = this.quantityFromPurchaseOrderItem(it);
      return {
        purchaseOrderItemId: it.id,
        label: (it.productName || it.itemName || 'Item').trim() || 'Item',
        unit: (it.unit || '').trim(),
        quantityRequested: Number(it.quantityRequested || 0),
        actualUnitPrice: rounded,
        quantityApproved: qtyFromItem > 0 ? qtyFromItem : null
      };
    });
  }

  /** Quantidade enviada no select-winner: a do item do pedido (aprovada se já houver, senão a solicitada). */
  private quantityFromPurchaseOrderItem(it: PurchaseOrderItem): number {
    const qa = Number(it.quantityApproved);
    const qr = Number(it.quantityRequested);
    const base = Number.isFinite(qa) && qa > 0 ? qa : qr;
    const n = Math.floor(Number(base));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /** Prioriza campo unitário explícito; senão deriva de total da linha / quantidade cotada. */
  private unitPriceFromQuotationLine(ln: PurchaseOrderQuotationLine): number | null {
    const direct = ln.quotedUnitPrice;
    if (direct != null && Number.isFinite(Number(direct))) {
      return Number(direct);
    }
    const qty = ln.quantityQuoted;
    const total = ln.lineTotal;
    if (
      qty != null &&
      Number(qty) > 0 &&
      total != null &&
      Number.isFinite(Number(total))
    ) {
      return Number(total) / Number(qty);
    }
    return null;
  }

  /**
   * Quando a API não envia linhas: rateia o subtotal de itens da proposta proporcionalmente ao valor estimado do pedido.
   */
  private inferredUnitPriceFromItemsSubtotal(
    quotation: PurchaseOrderQuotation,
    item: PurchaseOrderItem,
    allItems: PurchaseOrderItem[]
  ): number | null {
    const sub = quotation.itemsSubtotal;
    if (sub == null || !Number.isFinite(Number(sub)) || Number(sub) <= 0) {
      return null;
    }
    const qty = Number(item.quantityRequested || 0);
    if (!(qty > 0)) {
      return null;
    }
    const sumEst = allItems.reduce((s, x) => s + Number(x.estimatedTotalPrice || 0), 0);
    if (sumEst > 0) {
      const share = (Number(item.estimatedTotalPrice || 0) / sumEst) * Number(sub);
      return share / qty;
    }
    const totalQty = allItems.reduce((s, x) => s + Number(x.quantityRequested || 0), 0);
    if (totalQty > 0) {
      const share = Number(sub) * (qty / totalQty);
      return share / qty;
    }
    return null;
  }

  confirmWinner(): void {
    if (!this.header || !this.staffId || !this.winnerQuotationId) return;
    if (this.isLoadingWinnerLines || !this.winnerLinesHaveValidPrices()) {
      this.errorService.showErrorMessage(
        'Aguarde o carregamento ou verifique se todos os itens têm preço no orçamento da proposta vencedora.'
      );
      return;
    }
    const reason = this.winnerReason.trim();
    if (!reason) {
      this.errorService.showErrorMessage('Informe o motivo da escolha.');
      return;
    }
    if (!this.winnerLines.length) {
      this.errorService.showErrorMessage(
        'O pedido não possui itens para enviar na escolha do vencedor. Recarregue o pedido e tente novamente.'
      );
      return;
    }
    const linesPayload: SelectWinnerDto['lines'] = [];
    for (const row of this.winnerLines) {
      const qty = Number(row.quantityApproved ?? 0);
      const price = Number(row.actualUnitPrice ?? NaN);
      if (!Number.isFinite(qty) || qty <= 0) {
        this.errorService.showErrorMessage(
          'Quantidade inválida em algum item do pedido. Recarregue o pedido e verifique as quantidades nos itens.'
        );
        return;
      }
      if (!Number.isFinite(price) || price < 0) {
        this.errorService.showErrorMessage(
          'Preço unitário ausente ou inválido em algum item — deve vir do orçamento da proposta.'
        );
        return;
      }
      linesPayload.push({
        purchaseOrderItemId: row.purchaseOrderItemId,
        actualUnitPrice: Math.round((price + Number.EPSILON) * 100) / 100,
        quantityApproved: Math.floor(qty)
      });
    }
    const body: SelectWinnerDto = {
      quotationId: this.winnerQuotationId,
      staffId: this.staffId,
      selectionReason: reason,
      directorshipApproverStaffId: null,
      lines: linesPayload
    };
    this.isSelectingWinner = true;
    this.quotationsService
      .selectWinner(this.header.id, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSelectingWinner = false;
          this.showWinnerModal = false;
          this.winnerQuotationId = null;
          this.winnerLines = [];
          this.errorService.showSuccessMessage('Fornecedor vencedor selecionado. O pedido pode ter sido atualizado.');
          if (this.header) this.loadHeaderDetail(this.header.id);
          if (this.purchaseOrderId) {
            this.purchaseOrderService.getPurchaseOrderById(this.purchaseOrderId).pipe(takeUntil(this.destroy$)).subscribe({
              next: (o) => (this.purchaseOrder = o)
            });
          }
        },
        error: () => {
          this.isSelectingWinner = false;
          this.errorService.showErrorMessage('Não foi possível selecionar o vencedor.');
        }
      });
  }

  openCancelProposal(q: PurchaseOrderQuotation): void {
    if (!this.canMutateProposals() || q.isWinner) return;
    this.cancelProposalId = q.id;
    this.cancelProposalReason = '';
    this.showCancelProposalModal = true;
  }

  closeCancelProposalModal(): void {
    this.showCancelProposalModal = false;
    this.cancelProposalId = null;
  }

  confirmCancelProposal(): void {
    if (!this.staffId || !this.cancelProposalId) return;
    const reason = this.cancelProposalReason.trim();
    if (!reason) {
      this.errorService.showErrorMessage('Informe o motivo do cancelamento.');
      return;
    }
    this.quotationsService
      .cancelQuotation(this.cancelProposalId, { staffId: this.staffId, reason })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeCancelProposalModal();
          this.errorService.showInfoMessage('Proposta cancelada.');
          if (this.header) this.refreshProposalsList(this.header.id);
        },
        error: () => this.errorService.showErrorMessage('Não foi possível cancelar a proposta.')
      });
  }

  deleteProposal(q: PurchaseOrderQuotation): void {
    if (!this.canMutateProposals() || q.isWinner) return;
    if (!confirm(`Excluir a proposta ${q.quotationNumber || '#' + q.id}? Esta ação não pode ser desfeita.`)) {
      return;
    }
    this.quotationsService
      .deleteQuotation(q.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showSuccessMessage('Proposta excluída.');
          if (this.header) this.refreshProposalsList(this.header.id);
        },
        error: () => this.errorService.showErrorMessage('Não foi possível excluir a proposta.')
      });
  }

  openCancelHeaderModal(): void {
    if (!this.header || !this.canCancelHeader()) return;
    this.cancelHeaderReason = '';
    this.showCancelHeaderModal = true;
  }

  closeCancelHeaderModal(): void {
    this.showCancelHeaderModal = false;
  }

  confirmCancelHeader(): void {
    if (!this.header || !this.staffId) return;
    const reason = this.cancelHeaderReason.trim();
    if (!reason) {
      this.errorService.showErrorMessage('Informe o motivo do cancelamento.');
      return;
    }
    this.quotationsService
      .cancelHeader(this.header.id, { staffId: this.staffId, reason })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeCancelHeaderModal();
          this.errorService.showInfoMessage('Processo de cotação cancelado.');
          this.reloadAll();
        },
        error: () => this.errorService.showErrorMessage('Não foi possível cancelar o processo.')
      });
  }

  deleteHeader(): void {
    if (!this.header || !this.canDeleteHeader()) return;
    if (
      !confirm(
        'Excluir definitivamente este processo de cotação? Só é permitido quando não há vencedor selecionado.'
      )
    ) {
      return;
    }
    const hid = this.header.id;
    this.quotationsService
      .deleteHeader(hid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showSuccessMessage('Processo removido.');
          this.header = null;
          this.proposals = [];
          this.reloadAll();
        },
        error: () => this.errorService.showErrorMessage('Não foi possível excluir o processo.')
      });
  }

  /** Nome do fornecedor na cotação: catálogo GET /suppliers; fallback ao nome vindo da API da proposta. */
  supplierLabel(q: PurchaseOrderQuotation): string {
    const sid = q.supplierId;
    const s = this.suppliers.find((x) => x.id === sid);
    if (s) {
      const label = `${(s.companyName || '').trim()}${s.tradeName ? ' — ' + s.tradeName.trim() : ''}`.trim();
      if (label) return label;
    }
    const apiName = (q.supplierName || '').trim();
    if (apiName) return apiName;
    return `#${sid}`;
  }

  /** Código interno da proposta (gerado no cliente; não depende de centro de custo). */
  private generateQuotationNumber(): string {
    const rand = Math.floor(100000 + Math.random() * 900000);
    return `PROP-${Date.now()}-${rand}`;
  }

  formatMoney(v?: number | null): string {
    if (v === undefined || v === null || Number.isNaN(Number(v))) return '—';
    return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  estimatedItemsTotal(): number {
    const items = this.purchaseOrder?.items || [];
    return items.reduce((sum, item) => sum + Number(item.estimatedTotalPrice || 0), 0);
  }

  /** Proposta vencedora atual, ou `null` se ainda não houve seleção. */
  get winnerProposal(): PurchaseOrderQuotation | null {
    return this.proposals.find((p) => p.isWinner) || null;
  }

  /**
   * Valor total do item segundo a **proposta vencedora**.
   * Estratégia (em cascata):
   *  1. Se a linha da proposta tem `lineTotal`, usa direto.
   *  2. Senão, tenta `unitPrice * quantity` da linha.
   *  3. Senão, rateia o `itemsSubtotal` da proposta proporcionalmente.
   *  4. Senão, devolve `null` (sem dado suficiente).
   */
  winnerTotalForItem(item: PurchaseOrderItem): number | null {
    const winner = this.winnerProposal;
    if (!winner || !item) return null;

    const line = (winner.lines || []).find((l) => l.purchaseOrderItemId === item.id) || null;
    const qtyItem = this.quantityFromPurchaseOrderItem(item);

    if (line) {
      const total = Number(line.lineTotal);
      if (Number.isFinite(total) && total > 0) return total;

      const unit = this.unitPriceFromQuotationLine(line);
      if (unit != null) {
        const qty = Number(line.quantityQuoted) > 0 ? Number(line.quantityQuoted) : qtyItem;
        if (qty > 0) return Math.round((unit * qty + Number.EPSILON) * 100) / 100;
      }
    }

    const allItems = this.purchaseOrder?.items || [];
    const inferredUnit = this.inferredUnitPriceFromItemsSubtotal(winner, item, allItems);
    if (inferredUnit != null && qtyItem > 0) {
      return Math.round((inferredUnit * qtyItem + Number.EPSILON) * 100) / 100;
    }
    return null;
  }

  /** Valor exibido na coluna: usa o vencedor quando houver, senão o estimado original. */
  displayItemTotal(item: PurchaseOrderItem): number | null {
    if (this.winnerProposal) {
      return this.winnerTotalForItem(item);
    }
    const v = Number(item.estimatedTotalPrice);
    return Number.isFinite(v) ? v : null;
  }

  /** Soma do total exibido para todos os itens (vencedor ou estimado). */
  displayItemsTotal(): number {
    const items = this.purchaseOrder?.items || [];
    return items.reduce((sum, it) => sum + Number(this.displayItemTotal(it) ?? 0), 0);
  }

  /** Cabeçalho/rodapé adaptativo conforme há ou não vencedor selecionado. */
  get itemsTotalColumnLabel(): string {
    return this.winnerProposal ? 'Valor (proposta vencedora)' : 'Valor Estimado';
  }

  get itemsTotalFooterLabel(): string {
    return this.winnerProposal ? 'Total do pedido (proposta vencedora):' : 'Total estimado do pedido:';
  }

  formatWhen(iso?: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('pt-BR');
  }

  proposalSlotsRemaining(): number {
    return Math.max(0, MAX_PROPOSALS - this.proposals.length);
  }

  hasWinner(): boolean {
    return this.proposals.some((p) => p.isWinner);
  }

  isHeaderCancelled(h?: PurchaseOrderQuotationHeader | null): boolean {
    const s = (h?.status || '').toLowerCase();
    return s === 'cancelled' || s === 'canceled' || s === 'cancelado';
  }

  isHeaderOpen(h?: PurchaseOrderQuotationHeader | null): boolean {
    const s = (h?.status || '').toLowerCase();
    return s === 'open' || s === 'aberto';
  }

  headerLocked(): boolean {
    if (!this.header) return false;
    if (this.isHeaderCancelled(this.header)) return true;
    const st = (this.header.status || '').toLowerCase();
    if (st && st !== 'open' && st !== 'aberto') return true;
    return this.hasWinner();
  }

  canMutateProposals(): boolean {
    return Boolean(this.header && !this.headerLocked());
  }

  canSelectWinner(): boolean {
    return this.canMutateProposals() && this.proposals.length > 0;
  }

  canCancelHeader(): boolean {
    return Boolean(this.header && !this.isHeaderCancelled(this.header) && !this.hasWinner());
  }

  canDeleteHeader(): boolean {
    return this.canCancelHeader();
  }

  // ===== PORTAL DE ANEXOS DA PROPOSTA =====

  /** Abre o modal com a lista de anexos da proposta e carrega o histórico. */
  openDocumentsModal(q: PurchaseOrderQuotation): void {
    if (!q?.id) return;
    this.documentsModalQuotation = q;
    this.documents = [];
    this.documentForm = this.emptyDocumentForm();
    this.showDocumentsModal = true;
    this.loadDocumentsList();
  }

  closeDocumentsModal(): void {
    this.showDocumentsModal = false;
    this.documentsModalQuotation = null;
    this.documents = [];
    this.documentForm = this.emptyDocumentForm();
  }

  /** GET /purchase-order-quotations/{id}/documents — também atualiza o badge. */
  loadDocumentsList(): void {
    const q = this.documentsModalQuotation;
    if (!q?.id) return;
    this.isLoadingDocuments = true;
    this.quotationsService
      .listDocuments(q.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (docs) => {
          this.documents = docs || [];
          this.documentsCountByQuotation = {
            ...this.documentsCountByQuotation,
            [q.id]: this.documents.length
          };
          this.isLoadingDocuments = false;
        },
        error: () => {
          this.documents = [];
          this.isLoadingDocuments = false;
          this.errorService.showErrorMessage(
            'Não foi possível carregar os anexos da proposta.'
          );
        }
      });
  }

  /** Captura o arquivo selecionado e valida o tamanho antes do envio. */
  onDocumentFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files.length ? input.files[0] : null;
    if (!file) {
      this.documentForm.file = null;
      return;
    }
    if (file.size > this.maxDocumentSizeBytes) {
      const mb = (this.maxDocumentSizeBytes / 1024 / 1024).toFixed(0);
      this.errorService.showErrorMessage(
        `O arquivo ultrapassa o limite de ${mb} MB.`
      );
      input.value = '';
      this.documentForm.file = null;
      return;
    }
    this.documentForm.file = file;
  }

  /** Sobe o arquivo no blob e em sequência registra os metadados na cotação. */
  submitDocumentUpload(): void {
    const q = this.documentsModalQuotation;
    if (!q?.id) return;
    if (!this.staffId) {
      this.errorService.showErrorMessage(
        'Não foi possível identificar o usuário logado.'
      );
      return;
    }

    const { file, category, description } = this.documentForm;
    if (!file) {
      this.errorService.showErrorMessage('Selecione um arquivo para enviar.');
      return;
    }
    if (!category) {
      this.errorService.showErrorMessage('Selecione a categoria do anexo.');
      return;
    }

    this.isUploadingDocument = true;
    this.quotationsService
      .uploadAndAttachDocument(q.id, file, {
        category,
        description: description.trim() || undefined,
        uploadedByStaffId: this.staffId
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isUploadingDocument = false;
          this.documentForm = this.emptyDocumentForm();
          this.errorService.showSuccessMessage('Anexo enviado com sucesso.');
          this.loadDocumentsList();
        },
        error: (err) => {
          this.isUploadingDocument = false;
          const msg =
            err?.message ||
            err?.error?.error ||
            'Não foi possível enviar o anexo.';
          this.errorService.showErrorMessage(msg);
        }
      });
  }

  deleteQuotationDocument(d: QuotationDocument): void {
    if (!d?.id) return;
    if (!confirm(`Remover o anexo «${d.fileName || ''}»?`)) return;
    this.deletingDocumentId = d.id;
    this.quotationsService
      .deleteDocument(d.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingDocumentId = null;
          this.errorService.showSuccessMessage('Anexo removido.');
          this.loadDocumentsList();
        },
        error: () => {
          this.deletingDocumentId = null;
          this.errorService.showErrorMessage(
            'Não foi possível remover o anexo.'
          );
        }
      });
  }

  /** Retorna a quantidade de anexos visíveis para a badge (0 se ainda não consultado). */
  documentCount(quotationId: number | null | undefined): number {
    if (quotationId == null) return 0;
    return this.documentsCountByQuotation[quotationId] || 0;
  }

  /** Ícone Font Awesome baseado no contentType/extensão do anexo. */
  documentIconClass(d: QuotationDocument): string {
    const type = (d?.contentType || '').toLowerCase();
    const name = (d?.fileName || '').toLowerCase();
    if (type.includes('pdf') || name.endsWith('.pdf')) {
      return 'fas fa-file-pdf text-danger';
    }
    if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/.test(name)) {
      return 'fas fa-file-image text-primary';
    }
    if (type.includes('spreadsheet') || /\.(xlsx?|csv|ods)$/.test(name)) {
      return 'fas fa-file-excel text-success';
    }
    if (type.includes('word') || /\.(docx?)$/.test(name)) {
      return 'fas fa-file-word text-primary';
    }
    if (type.includes('zip') || /\.(zip|rar|7z|tar|gz)$/.test(name)) {
      return 'fas fa-file-archive text-warning';
    }
    return 'fas fa-file text-secondary';
  }

  openDocumentLink(d: QuotationDocument): void {
    if (!d?.filePath) return;
    window.open(d.filePath, '_blank', 'noopener,noreferrer');
  }

  /** Texto da categoria traduzido para o pt-BR (lista fixa). */
  documentCategoryLabel(category?: string | null): string {
    if (!category) return '—';
    const found = this.documentCategoryOptions.find((c) => c.value === category);
    return found?.label || category;
  }

  private emptyDocumentForm(): QuotationDocumentFormModel {
    return {
      file: null,
      category: QuotationDocumentCategory.ORCAMENTO,
      description: ''
    };
  }

  private emptyProposalForm(): ProposalFormModel {
    const now = new Date();
    const until = new Date(now);
    until.setMonth(until.getMonth() + 1);
    return {
      supplierId: null,
      quotationNumber: '',
      quotationDate: this.toInputIso(now.toISOString()),
      validUntil: this.toInputIso(until.toISOString()),
      currencyCode: CurrencyCode.BRL,
      itemsSubtotal: null,
      discountAmount: 0,
      freightAmount: null,
      otherCostsAmount: 0,
      taxAmount: null,
      totalAmount: null,
      paymentTermDays: 30,
      deliveryLeadTimeDays: 7,
      status: 'Received',
      notes: ''
    };
  }

  /** datetime-local (sem timezone) a partir de ISO da API */
  private toInputIso(iso?: string): string {
    if (!iso) {
      const d = new Date();
      return d.toISOString().slice(0, 16);
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 16);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private fromInputIso(localVal: string): string {
    if (!localVal) return new Date().toISOString();
    const d = new Date(localVal);
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  }
}
